import React, { useState, useEffect } from "react"
import { useForm, useFieldArray } from "react-hook-form"
import { Form, Button, Row, Col, Alert } from "react-bootstrap"
import StateList from "./StateList"
import CityList from "./CityList"
import TravelAssistanceForm from "./TravelAssistanceForm"
import PlusSymbol from "./plus-symbol.svg"
import MinusSymbol from "./minus-symbol.svg"
import ModifyForm from "./ModifyForm"

export default function CommissioningRequestForm({ onSubmitSuccess }) {
  const [travelAssistance, setTravelAssitance] = useState(false)
  const [travelAssistanceData, setTravelAssistanceData] = useState({})

  const {
    register,
    formState: { errors },
    reset,
    //errors,
    watch,
    handleSubmit,
    getValues,
    control,
    setValue,
  } = useForm({
    mode: "onSubmit",
    defaultValues: {
      number_of_tickets: "",
      requester_prefix: "",
      requester_first_name: "",
      requester_last_name: "",
      requester_suffix: "",
      requester_email: "",
      email_verified: false,
      requester_phone: "",
      requester_street_address_1: "",
      requester_street_address_2: "",
      requester_street_address_3: "",
      requester_city: "",
      requester_other_city: "",
      requester_state: "",
      requester_zip: "",
      ticket_for_self: "",
      person: [
        {
          prefix: "",
          email: "",
          first_name: "",
          last_name: "",
          suffix: "",
          phone: "",
          street_address_1: "",
          street_address_2: "",
          street_address_3: "",
          city: "",
          other_city: "",
          state: "",
          zip: "",
          relationship: "",
          drivers_license_state: "",
          drivers_license_number: "",
          optional_info: "",
          date_of_birth: "",
          city_of_birth: "",
          county_of_birth: "",
          state_of_birth: "",
          country_of_birth: "",
          us_citizen: "",
          country_of_citizenship: "",
          other_country_of_citizenship: "",
          permanent_resident: "",
          green_card_number: "",
          id_type: "",
          federal_id_type: "",
          federal_id_number: "",
          us_passport: "",
          passport_number: "",
          other_country_passport: "",
          other_country_passport_number: "",
          veteran: "",
          branch: "",
          other_branch: "",
          status: "",
          rank: "",
          dod_id_number: "",
          comments: "",
          veteran_service_org: [],
          other_veteran_service_org: "",
          subvet_base: "",
          travel_assistance: "",
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: "person",
  })

  const watchObject = watch()

  // Clean up person array when number of tickets changes
  useEffect(() => {
    const currentTickets = watchObject.number_of_tickets === "other" 
      ? parseInt(watchObject.other_number_of_tickets) || 0
      : parseInt(watchObject.number_of_tickets) || 0
    
    const currentPersonData = watchObject.person || []
    
    // Only clean up if we have more person entries than tickets requested
    if (currentPersonData.length > currentTickets && currentTickets > 0) {
      // Keep only the number of persons matching the ticket count
      const cleanedPersonData = currentPersonData.slice(0, currentTickets)
      setValue("person", cleanedPersonData)
    }
  }, [watchObject.number_of_tickets, watchObject.other_number_of_tickets, watchObject.person, setValue])

  const [succeeded, setSucceeded] = useState(false)
  const [error, setError] = useState(null)
  const [processing, setProcessing] = useState("")
  const [disableSubmit, setDisableSubmit] = useState(false)
  const [submissionError, setSubmissionError] = useState(null)
  const [submittedEmail, setSubmittedEmail] = useState("")

  const [showSuccessMessage, setShowSuccessMessage] = useState(false)

  const toggleSuccessMessage = show => {
    setShowSuccessMessage(show)
  }

  const Required = () => <span style={styles.formError}>*</span>

  const styles = {
    formError: {
      color: "red",
      fontSize: "0.8rem",
      fontWeight: 700,
    },
    formLabel: {
      fontWeight: 700,
      fontSize: "1.125rem",
    },
    disclaimer: {
      fontSize: "1.125rem",
      color: "black",
      marginBottom: 0,
    },
  }

  const handleChange = async event => {
    setError(event.error ? event.error.message : "")
    //console.log(watchObject)
  }

  const processFormData = data => {
    // Clone the data object to avoid mutating the original
    const processedData = { ...data }

    // Check if person is an array
    if (Array.isArray(processedData.person)) {
      // Iterate over each person object
      processedData.person = processedData.person.map(person => {
        // Check if veteran_service_org is an array
        if (Array.isArray(person.veteran_service_org)) {
          // Convert the array to a comma-delimited string
          person.veteran_service_org = person.veteran_service_org.join(", ")
        }
        return person
      })
    }

    return processedData
  }

  const submitForm = async data => {
    // Process the form data
    const processedData = processFormData(data)

    setProcessing(true)
    setDisableSubmit(true)
    setSubmissionError(null)

    try {
      // Step 1: Send email notification via SES
      const emailResponse = await fetch("/.netlify/functions/ses-commissioning-request", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(processedData),
      })

      if (!emailResponse.ok) {
        const errorData = await emailResponse.json().catch(() => ({}))
        throw new Error(errorData.error || `Email service error (${emailResponse.status})`)
      }

      const emailResult = await emailResponse.json()
      console.log("Email sent successfully:", emailResult.messageId)

      // Step 2: Submit to Excel spreadsheet
      try {
        const excelResponse = await fetch("/.netlify/functions/submit-commissioning-to-excel", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(processedData),
        })

        if (!excelResponse.ok) {
          const errorText = await excelResponse.text()
          console.warn("Excel write failed:", excelResponse.status, errorText)
          // Don't throw - we'll still show success since email was sent
        } else {
          const excelResult = await excelResponse.json()
          console.log("Excel write succeeded:", excelResult)
        }
      } catch (excelError) {
        console.warn("Excel write error:", excelError?.message || excelError)
        // Don't throw - we'll still show success since email was sent
      }

      // Success!
      // Save email before resetting form
      setSubmittedEmail(processedData.requester_email)
      setSucceeded(true)
      setProcessing(false)
      reset()
      
      // Notify parent component of successful submission
      if (onSubmitSuccess) {
        onSubmitSuccess()
      }

      // Handle travel assistance if requested
      if (processedData.travel_assistance === "Yes") {
        setTravelAssistanceData(processedData)
        setTravelAssitance(true)
      }

    } catch (error) {
      console.error("Submission error:", error)
      setSucceeded(false)
      setProcessing(false)
      setDisableSubmit(false)
      setSubmissionError(
        error.message || "An unexpected error occurred. Please try again or contact support."
      )
    }
  }

  const SubmitSuccessMessage = () => (
    <div className="submit-success-message text-center">
      <Alert variant="success">
        <h3>✅ Thank you for your submission!</h3>
        <div style={{ margin: "20px 0", padding: "15px", backgroundColor: "#e8f4f8", borderRadius: "8px" }}>
          <p style={{ marginBottom: "10px" }}><strong>IMPORTANT: Please Check Your Email</strong></p>
          <p style={{ fontSize: "1.2rem", color: "#0066cc", marginBottom: "10px" }}>
            A confirmation email has been sent to:<br />
            <strong>{submittedEmail}</strong>
          </p>
          <p style={{ fontSize: "0.9rem", color: "#555" }}>
            Please check your inbox (and spam/junk folder) for confirmation within the next 5 minutes.<br />
            If you don't receive it, the email address may be incorrect.
          </p>
        </div>
        <p>An electronic RSVP will be sent several weeks before the Commissioning by the U.S. Navy if you are approved by the U.S. Navy to attend.</p>
        <p>
        Tickets to the Commissioning of USS IDAHO SSN 799 are controlled by and at the discretion of the U.S. Navy. The USS IDAHO Commissioning Committee will do everything it can do to advocate on your behalf but we cannot make any guarantees, implied or otherwise, as to whether you will be offered and/or receive a ticket or the number of tickets you requested from the U.S. Navy.
        </p>
      </Alert>
    </div>
  )
  return (
    <>
      {succeeded && !travelAssistance ? (
        <>
          <SubmitSuccessMessage />
        </>
      ) : travelAssistance ? (
        <>
          <TravelAssistanceForm
            travelAssistanceData={travelAssistanceData}
            toggleSuccessMessage={toggleSuccessMessage}
          />
        </>
      ) : (
        <>
          <Alert variant="warning">
            <div style={styles.disclaimer}>
              <p style={{ fontSize: "1.125rem", fontWeight: "bold" }}>
                Tickets to the Commissioning ceremony of USS IDAHO (SSN 799) are
                controlled by and at the discretion of the U.S. Navy.
              </p>
              <p style={{ fontSize: "1.125rem", fontWeight: "bold" }}>
                The Commissioning is scheduled to be held on an active U.S. Navy
                Submarine Base in Groton Connecticut.
              </p>
              <p>
                All guests must have on hand a current federally recognized
                identification and U.S. Navy issued ticket to the
                Commissioning ceremony to enter Submarine Base New London in
                Groton.
              </p>
              <p>Examples of federally acceptable forms of ID:</p>
              <ul style={{ fontSize: "1rem" }}>
                <li>Federal issued ID (DoD, DoW, DoE, DoS, etc.)</li>
                <li>Passport</li>
                <li>
                  <a
                    href="https://www.dhs.gov/archive/real-id-public-faqs"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    REAL ID
                  </a>{" "}
                  (Star Card) Driver’s License
                </li>
              </ul>
              <p>Minors with a U.S. Navy issued ticket to the Commissioning, without a federally recognized identification, may attend the ceremony as long as they are accompanied their parent or legal guardian that has a federally recognized identification.</p>
              <p>With limited exceptions, U.S. Citizenship or U.S. Permanent Residence Status are required to attend the Commissioning.</p>
              <p>The USS IDAHO Commissioning Committee will forward your request to the U.S. Navy, but we cannot make any guarantees, implied or otherwise, as to whether you will be offered and/or receive a ticket or the number of tickets you requested from the U.S. Navy to attend the Commissioning ceremony.</p>
              <p>In the ticket request form to follow, you will be asked for the numbers of one of the IDs listed. Please be ready to provide before proceeding.</p>
              <p style={{ fontSize: "0.8rem" }}>
              Abbreviations:<br />
DoD Department of Defense<br />
DoW Department of War<br />
DoE Department of Energy<br />
DoS Department of State

              </p>
            </div>
            <div className="mt-4">
              <Form.Group style={{ marginBottom: 0 }}>
                <fieldset>
                  <input
                    className="radio"
                    type="checkbox"
                    inline
                    label="Yes"
                    id={`acknowledge_yes`}
                    name={`acknowledge_yes`}
                    value="Yes"
                    {...register(`acknowledge`)}
                  />
                  <label
                    className="radioLabel"
                    htmlFor={`acknowledge_yes`}
                    style={{
                      color: "black",
                      fontWeight: "bold",
                      fontSize: "larger",
                    }}
                  >
                    Check this Box to Acknowledge Your Understanding and Intention to Comply with the U.S. Navy Requirements
                  </label>
                </fieldset>
              </Form.Group>
            </div>
          </Alert>
          {watchObject.acknowledge === "Yes" && (
            <div className="fade-in">
              <p>
                <strong>
                  Invites and RSVP will ONLY be sent to the Requester.
                </strong>
              </p>
              <p>
                <Required /> <strong>Indicates a required field.</strong>
              </p>

              {submissionError && (
                <Alert variant="danger" onClose={() => setSubmissionError(null)} dismissible>
                  <Alert.Heading>Submission Error</Alert.Heading>
                  <p>{submissionError}</p>
                  <p style={{ fontSize: "0.9rem", marginBottom: 0 }}>
                    If this problem persists, please contact us directly at{" "}
                    <a href="mailto:support@ussidahocommittee.org">support@ussidahocommittee.org</a>
                  </p>
                </Alert>
              )}

              <form
                onSubmit={handleSubmit(submitForm)}
                id="new-request-form"
                name="new-request-form"
                style={{ fontSize: "1.125rem" }}
              >
                <div>
                  <div
                    style={{
                      marginBottom: "2rem",
                      border: "1px solid lightgray",
                      padding: "1rem",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <Row>
                      <Col>
                        <h4>Requester Information</h4>
                      </Col>
                    </Row>
                    <Form.Control
                      type="hidden"
                      {...register("timestamp")}
                      value={new Date().toISOString()}
                    />
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>
                            How many tickets to the USS IDAHO{" "}
                            <strong>Commissioning</strong> are you requesting
                            from the U.S. Navy?
                            <Required />
                          </Form.Label>
                          <Form.Row>
                            <Col sm={3}>
                              <Form.Control
                                as="select"
                                id="number_of_tickets"
                                name="number_of_tickets"
                                {...register("number_of_tickets", {
                                  required: "Number of tickets is required.",
                                })}
                              >
                                <option value="">Select...</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                                <option value="4">4</option>
                                <option value="5">5</option>
                                <option value="6">6</option>
                                <option value="7">7</option>
                                <option value="8">8</option>
                                <option value="9">9</option>
                                <option value="10">10</option>
                                <option value="11">11</option>
                                <option value="12">12</option>
                                <option value="other">Other (Specify)</option>
                              </Form.Control>
                              {errors.number_of_tickets && (
                                <span style={styles.formError}>
                                  {errors.number_of_tickets.message}
                                </span>
                              )}
                            </Col>
                          </Form.Row>
                        </Form.Group>
                      </Col>
                    </Row>
                    {watchObject.number_of_tickets === "other" ? (
                      <Row className="fade-in">
                        <Col>
                          <Form.Group>
                            <Form.Label>
                              Please specify the number of tickets you are
                              requesting
                            </Form.Label>
                            <Form.Row>
                              <Col sm={3}>
                                <Form.Control
                                  type="number"
                                  name="other_number_of_tickets"
                                  placeholder="13"
                                  min={13}
                                  {...register("other_number_of_tickets")}
                                />
                              </Col>
                            </Form.Row>
                          </Form.Group>
                        </Col>
                      </Row>
                    ) : null}
                    <Row>
                      <Col sm={12} md={3}>
                        <Form.Group>
                          <Form.Label>
                            Requester Prefix
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            as="select"
                            name="requester_prefix"
                            {...register("requester_prefix")}
                          >
                            <option value="">Select...</option>
                            <option value="Mr.">Mr.</option>
                            <option value="Mrs.">Mrs.</option>
                            <option value="Mr. and Mrs.">Mr. and Mrs.</option>
                            <option value="Ms.">Ms.</option>
                            <option value="Miss">Miss</option>
                            <option value="Master">Master</option>
                            <option value="Dr.">Dr.</option>
                            <option value="Dr. and Dr.">Dr. and Dr.</option>
                            <option value="Dr. and Mr.">Dr. and Mr.</option>
                            <option value="Dr. and Mrs.">Dr. and Mrs.</option>
                            <option value="The Honorable">The Honorable</option>
                            <option value="The Honorable and Mr.">The Honorable and Mr.</option>
                            <option value="The Honorable and Mrs.">The Honorable and Mrs.</option>
                            <option value="Rear Admiral">Rear Admiral</option>
                            <option value="Vice Admiral">Vice Admiral</option>
                            <option value="Admiral">Admiral</option>
                            <option value="Captain">Captain</option>
                            <option value="Commander">Commander</option>
                            <option value="Lieutenant Commander">Lieutenant Commander</option>
                            <option value="Lieutenant">Lieutenant</option>
                            <option value="Lieutenant Junior Grade">Lieutenant Junior Grade</option>
                            <option value="Ensign">Ensign</option>
                            <option value="Commodore">Commodore</option>
                            <option value="Command Master Chief">Command Master Chief</option>
                            <option value="Master Chief Petty Officer">Master Chief Petty Officer</option>
                            <option value="Senior Chief Petty Officer">Senior Chief Petty Officer</option>
                            <option value="Chief Petty Officer">Chief Petty Officer</option>
                            <option value="Petty Officer First Class">Petty Officer First Class</option>
                            <option value="Petty Officer Second Class">Petty Officer Second Class</option>
                            <option value="Petty Officer Third Class">Petty Officer Third Class</option>
                            <option value="Seaman">Seaman</option>
                            <option value="Seaman Apprentice">Seaman Apprentice</option>
                            <option value="Seaman Recruit">Seaman Recruit</option>
                            <option value="Master Chief Petty Officer of the Navy">Master Chief Petty Officer of the Navy</option>
                            <option value="General">General</option>
                            <option value="Lieutenant General">Lieutenant General</option>
                            <option value="Major General">Major General</option>
                            <option value="Brigadier General">Brigadier General</option>
                            <option value="Colonel">Colonel</option>
                            <option value="Lieutenant Colonel">Lieutenant Colonel</option>
                            <option value="Major">Major</option>
                            <option value="First Lieutenant">First Lieutenant</option>
                            <option value="Second Lieutenant">Second Lieutenant</option>
                            <option value="Chief Warrant Officer 5">Chief Warrant Officer 5</option>
                            <option value="Chief Warrant Officer 4">Chief Warrant Officer 4</option>
                            <option value="Chief Warrant Officer 3">Chief Warrant Officer 3</option>
                            <option value="Chief Warrant Officer 2">Chief Warrant Officer 2</option>
                            <option value="Warrant Officer 1">Warrant Officer 1</option>
                            <option value="Master Gunnery Sergeant">Master Gunnery Sergeant</option>
                            <option value="Sergeant Major">Sergeant Major</option>
                            <option value="First Sergeant">First Sergeant</option>
                            <option value="Master Sergeant">Master Sergeant</option>
                            <option value="Gunnery Sergeant">Gunnery Sergeant</option>
                            <option value="Staff Sergeant">Staff Sergeant</option>
                            <option value="Sergeant">Sergeant</option>
                            <option value="Corporal">Corporal</option>
                            <option value="Lance Corporal">Lance Corporal</option>
                            <option value="Private First Class">Private First Class</option>
                            <option value="Private">Private</option>
                            <option value="Command Sergeant Major">Command Sergeant Major</option>
                            <option value="Sergeant First Class">Sergeant First Class</option>
                            <option value="Specialist">Specialist</option>
                            <option value="Private Second Class">Private Second Class</option>
                            <option value="Technical Sergeant">Technical Sergeant</option>
                            <option value="Senior Airman">Senior Airman</option>
                            <option value="Airman First Class">Airman First Class</option>
                            <option value="Airman">Airman</option>
                            <option value="Airman Basic">Airman Basic</option>
                            <option value="Command Chief Master Sergeant">Command Chief Master Sergeant</option>
                            <option value="Chief Master Sergeant">Chief Master Sergeant</option>
                            <option value="Senior Master Sergeant of the Air Force">Senior Master Sergeant of the Air Force</option>
                            <option value="Sergeant Major of the Marine Corps">Sergeant Major of the Marine Corps</option>
                            <option value="Reverend">Reverend</option>
                            <option value="Bishop">Bishop</option>
                          </Form.Control>
                        </Form.Group>
                      </Col>
                      <Col sm={12} md={3}>
                        <Form.Group>
                          <Form.Label>
                            Requester First Name
                            <Required />
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            name="requester_first_name"
                            {...register("requester_first_name", {
                              required: true,
                            })}
                          />{" "}
                          {errors.requester_first_name && (
                            <span style={styles.formError}>
                              First Name is required.
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={12} md={4}>
                        <Form.Group>
                          <Form.Label>
                            Requester Last Name
                            <Required />
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            name="requester_last_name"
                            {...register("requester_last_name", {
                              required: true,
                            })}
                          />{" "}
                          {errors.requester_last_name && (
                            <span style={styles.formError}>
                              Last Name is required.
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={12} md={2}>
                        <Form.Group>
                          <Form.Label>
                            Requester Suffix
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            as="select"
                            name="requester_suffix"
                            {...register("requester_suffix")}
                          >
                            <option value="">Select...</option>
                            <option value="and Family">and Family</option>
                            <option value="and Guest">and Guest</option>
                            <option value="II">II</option>
                            <option value="III">III</option>
                            <option value="Jr.">Jr.</option>
                            <option value="Esq.">Esq.</option>
                            <option value="MD">MD</option>
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col sm={12} md={6}>
                        <Form.Group>
                          <Form.Label>
                            Requester Email Address
                            <Required />
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            placeholder="example@email.com"
                            {...register("requester_email", {
                              required: "Email is required.",
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                message: "Enter a valid e-mail address",
                              },
                            })}
                          />{" "}
                          {errors.requester_email && (
                            <span style={styles.formError}>
                              {errors.requester_email.message}
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={12} md={6}>
                        <Form.Group>
                          <Form.Label>
                            Requester Phone Number (mobile preferred)
                            <Required />
                          </Form.Label>
                          <Form.Control
                            //placeholder={errors.requester_phone ? errors.requester_phone.message : ""}
                            className="formField"
                            type="text"
                            name="requester_phone"
                            {...register("requester_phone", {
                              required: "Phone number is required.",
                              // pattern: {
                              //   value: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
                              //   message: "Please enter a valid phone number.",
                              // },
                            })}
                          />{" "}
                          {errors.requester_phone && (
                            <span style={styles.formError}>
                              {errors.requester_phone.message}
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>
                            Requester Street Address 1<Required />
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            {...register(`requester_street_address_1`, {
                              required: true,
                            })}
                          />
                          {errors.requester_street_address_1 && (
                            <span style={styles.formError}>
                              Street Address 1 is required.
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Requester Address 2</Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            {...register(`requester_street_address_2`)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Requester Address 3</Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            {...register(`requester_street_address_3`)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col sm={12} md={4}>
                        <Form.Group>
                          <Form.Label>
                            Requester City
                            <Required />
                          </Form.Label>
                          <Form.Control
                            as="select"
                            defaultValue="Select"
                            {...register(`requester_city`, { required: true })}
                          >
                            {CityList().cities.map(index => (
                              <option key={index} value={index}>
                                {index}
                              </option>
                            ))}
                          </Form.Control>
                          {errors.requester_city && (
                            <span style={styles.formError}>
                              City is required.
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      {watchObject.requester_city === "OTHER (Specify)" && (
                        <Col sm={12} md={4}>
                          <Form.Group>
                            <Form.Label>Other City</Form.Label>
                            <Form.Control
                              className="formField"
                              type="text"
                              {...register('requester_other_city')}
                            />
                          </Form.Group>
                        </Col>
                      )}
                      <Col sm={12} md={4}>
                        <Form.Group>
                          <Form.Label>
                            Requester State
                            <Required />
                          </Form.Label>
                          <Form.Control
                            as="select"
                            defaultValue="Idaho"
                            {...register(`requester_state`, { required: true })}
                          >
                            {StateList().states.map(index => (
                              <option key={index} value={index}>
                                {index}
                              </option>
                            ))}
                          </Form.Control>
                          {errors.requester_state && (
                            <span style={styles.formError}>
                              State is required.
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                      <Col sm={12} md={4}>
                        <Form.Group>
                          <Form.Label>
                            Requester Zip
                            <Required />
                          </Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            {...register(`requester_zip`, { required: true })}
                          />
                          {errors.requester_zip && (
                            <span style={styles.formError}>
                              Zip is required.
                            </span>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Company or organization</Form.Label>
                          <Form.Row>
                            <Col>
                              <Form.Control
                                as="select"
                                id="company_or_organization"
                                {...register("company_or_organization")}
                              >
                                <option value="">Select</option>
                                <option value="USS IDAHO Commissioning Committee">USS IDAHO Commissioning Committee</option>
                                <option value="USS IDAHO Commissioning Advisory Board">USS IDAHO Commissioning Advisory Board</option>
                                <option value="State of Idaho">State of Idaho</option>
                                <option value="State of Idaho, State Senate">State of Idaho, State Senate</option>
                                <option value="State of Idaho, House of Representatives">State of Idaho, House of Representatives</option>
                                <option value="Idaho Division of Veteran Services">Idaho Division of Veteran Services</option>
                                <option value="Idaho National Guard">Idaho National Guard</option>
                                <option value="US NAVY">US NAVY</option>
                                <option value="US MARINE CORPS">US MARINE CORPS</option>
                                <option value="US ARMY">US ARMY</option>
                                <option value="US AIRFORCE">US AIRFORCE</option>
                                <option value="Gold Star Family">Gold Star Family</option>
                                <option value="Blue Star Family">Blue Star Family</option>
                                <option value="Former PCU IDAHO Crew">Former PCU IDAHO Crew</option>
                                <option value="Kootenai Tribe of Idaho">Kootenai Tribe of Idaho</option>
                                <option value="Coeur d'Alene Tribe">Coeur d'Alene Tribe</option>
                                <option value="Nez Perce Tribe">Nez Perce Tribe</option>
                                <option value="Shoshone-Piute Tribes">Shoshone-Piute Tribes</option>
                                <option value="Shoshone-Bannock Tribes">Shoshone-Bannock Tribes</option>
                                <option value="Idaho Power">Idaho Power</option>
                                <option value="Albertsons">Albertsons</option>
                                <option value="United Heritage Insurance">United Heritage Insurance</option>
                                <option value="Citizens Community Bank">Citizens Community Bank</option>
                                <option value="First Interstate Bank">First Interstate Bank</option>
                                <option value="Con Paulos Chevrolet">Con Paulos Chevrolet</option>
                                <option value="Idaho Central Credit Union">Idaho Central Credit Union</option>
                                <option value="Agri Beef">Agri Beef</option>
                                <option value="J.A. and Kathryn Albertsons Foundation">J.A. and Kathryn Albertsons Foundation</option>
                                <option value="Mission 43">Mission 43</option>
                                <option value="Battelle Energy Alliance">Battelle Energy Alliance</option>
                                <option value="Hecla Mining Company">Hecla Mining Company</option>
                                <option value="Schweitzer Engineering Laboratories">Schweitzer Engineering Laboratories</option>
                                <option value="Delta Dental of Idaho">Delta Dental of Idaho</option>
                                <option value="JR Simplot Company">JR Simplot Company</option>
                                <option value="Boise Cascade">Boise Cascade</option>
                                <option value="Idaho Forest Group">Idaho Forest Group</option>
                                <option value="44 North Vodka">44 North Vodka</option>
                                <option value="Idaho Mercantile Distillers">Idaho Mercantile Distillers</option>
                                <option value="Shaw Shooting">Shaw Shooting</option>
                                <option value="Lockheed Martin">Lockheed Martin</option>
                                <option value="Avista">Avista</option>
                                <option value="Buck Knives">Buck Knives</option>
                                <option value="Litehouse Foods">Litehouse Foods</option>
                                <option value="Olympic Steel">Olympic Steel</option>
                                <option value="The Sign Center">The Sign Center</option>
                                <option value="River Club Boise">River Club Boise</option>
                                <option value="V Chocolates">V Chocolates</option>
                                <option value="General Dynamics Mission Systems">General Dynamics Mission Systems</option>
                                <option value="North American Brewers">North American Brewers</option>
                                <option value="BAE Systems">BAE Systems</option>
                                <option value="Super One Foods">Super One Foods</option>
                                <option value="Curtiss Wright">Curtiss Wright</option>
                                <option value="Tamarack Resort">Tamarack Resort</option>
                                <option value="SCS Wraps">SCS Wraps</option>
                                <option value="University Financial">University Financial</option>
                                <option value="Coleman Engineering">Coleman Engineering</option>
                                <option value="City of Soda Springs">City of Soda Springs</option>
                                <option value="Cohen Partners">Cohen Partners</option>
                                <option value="Northwest Specialty Hospital">Northwest Specialty Hospital</option>
                                <option value="Daughters of the American Revolution (DAR)">Daughters of the American Revolution (DAR)</option>
                                <option value="Sons of the American Revolution (SAR)">Sons of the American Revolution (SAR)</option>
                                <option value="Electric Boat">Electric Boat</option>
                                <option value="Huntington Ingalls Shipbuilding">Huntington Ingalls Shipbuilding</option>
                                <option value="Newport News Shipbuilding">Newport News Shipbuilding</option>
                                <option value="Idaho Defense Alliance">Idaho Defense Alliance</option>
                                <option value="Inlight Web Studio">Inlight Web Studio</option>
                                <option value="Idaho Public TV">Idaho Public TV</option>
                                <option value="Idaho State Historical Society">Idaho State Historical Society</option>
                                <option value="Sockeye Brewing Company">Sockeye Brewing Company</option>
                                <option value="Outer Light Brewing Company">Outer Light Brewing Company</option>
                                <option value="Idaho Brewing Company">Idaho Brewing Company</option>
                                <option value="Idaho Potato Commission">Idaho Potato Commission</option>
                                <option value="L3Harris">L3Harris</option>
                                <option value="Intermountain Gas &gt; MDU Resources">Intermountain Gas &gt; MDU Resources</option>
                                <option value="Rotary International">Rotary International</option>
                                <option value="Connie's Restaurant & Saloon Island Park">Connie's Restaurant &amp; Saloon Island Park</option>
                                <option value="Boise Hawks">Boise Hawks</option>
                                <option value="Idaho Falls Chukars">Idaho Falls Chukars</option>
                                <option value="National Cartoonists Society">National Cartoonists Society</option>
                                <option value="Family of US NAVY Veteran from USS IDAHO BB42">Family of US NAVY Veteran from USS IDAHO BB42</option>
                                <option value="University of Idaho">University of Idaho</option>
                                <option value="Idaho State University">Idaho State University</option>
                                <option value="Boise State University">Boise State University</option>
                                <option value="College of Idaho">College of Idaho</option>
                                <option value="Northwest Nazarene University">Northwest Nazarene University</option>
                                <option value="BYU - Idaho">BYU - Idaho</option>
                                <option value="Lewis and Clark State College">Lewis and Clark State College</option>
                                <option value="College of Southern Idaho">College of Southern Idaho</option>
                                <option value="North Idaho College">North Idaho College</option>
                                <option value="USS ARKANSAS SSN 800 Commissioning Committee">USS ARKANSAS SSN 800 Commissioning Committee</option>
                                <option value="USS UTAH SSN 801 Commissioning Committee">USS UTAH SSN 801 Commissioning Committee</option>
                                <option value="USS OKLAHOMA SSN 802 Commissioning Committee">USS OKLAHOMA SSN 802 Commissioning Committee</option>
                                <option value="USS ARIZONA SSN 803 Commissioning Committee">USS ARIZONA SSN 803 Commissioning Committee</option>
                                <option value="USS District of Columbia SSBN 826 Commissioning Committee">USS District of Columbia SSBN 826 Commissioning Committee</option>
                                <option value="USS WISCONSIN SSBN 827 Commissioning Committee">USS WISCONSIN SSBN 827 Commissioning Committee</option>
                                <option value="NA">NA</option>
                                <option value="OTHER">OTHER</option>
                              </Form.Control>
                              {/* {errors.company_or_organization && (
                              <span style={styles.formError}>
                                Company or organization is required.
                              </span>
                            )} */}
                            </Col>
                          </Form.Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    {watchObject.company_or_organization === "OTHER" && (
                      <Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>Other (please specify)</Form.Label>
                            <Form.Control
                              className="formField"
                              type="text"
                              {...register("other_company_or_organization")}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>Title</Form.Label>
                          <Form.Control
                            className="formField"
                            type="text"
                            name="title"
                            {...register("title")}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Form.Group>
                          <Form.Label>
                            If you are USS Idaho Commissioning Committee member,
                            which committee?
                          </Form.Label>
                          <Form.Row>
                            <Col sm={3}>
                              <Form.Control
                                as="select"
                                id="which_committee"
                                {...register("which_committee")}
                              >
                                <option value="">Select</option>
                                <option value="Planning">Planning</option>
                                <option value="Regional">Regional</option>
                                <option value="Marketing">Marketing</option>
                                <option value="Finance">Finance</option>
                                <option value="Fundraising">Fundraising</option>
                                <option value="Membership">Membership</option>
                                <option value="BoD">BoD</option>
                              </Form.Control>
                            </Col>
                          </Form.Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col>
                        <Alert variant="success" style={{ marginBottom: 0 }}>
                          <Form.Group>
                            <Form.Label style={{ color: "black" }}>
                              Are you, the requester, wanting a ticket for
                              yourself?
                            </Form.Label>
                            <fieldset>
                              <input
                                className="radio"
                                type="radio"
                                label="Yes"
                                id={`ticket_for_self_yes`}
                                value="Yes"
                                {...register("ticket_for_self")}
                                onChange={() => {
                                  setValue(
                                    `person[0].prefix`,
                                    watchObject.requester_prefix
                                  )
                                  setValue(
                                    `person[0].first_name`,
                                    watchObject.requester_first_name
                                  )
                                  setValue(
                                    `person[0].last_name`,
                                    watchObject.requester_last_name
                                  )
                                  setValue(
                                    `person[0].suffix`,
                                    watchObject.requester_suffix
                                  )
                                  setValue(
                                    `person[0].email`,
                                    watchObject.requester_email
                                  )
                                  setValue(
                                    `person[0].phone`,
                                    watchObject.requester_phone
                                  )
                                  setValue(
                                    `person[0].street_address_1`,
                                    watchObject.requester_street_address_1
                                  )
                                  setValue(
                                    `person[0].street_address_2`,
                                    watchObject.requester_street_address_2
                                  )
                                  setValue(
                                    `person[0].street_address_3`,
                                    watchObject.requester_street_address_3
                                  )
                                  setValue(
                                    `person[0].city`,
                                    watchObject.requester_city
                                  )
                                  setValue(
                                    `person[0].other_city`,
                                    watchObject.requester_other_city
                                  )
                                  setValue(
                                    `person[0].state`,
                                    watchObject.requester_state
                                  )
                                  setValue(
                                    `person[0].zip`,
                                    watchObject.requester_zip
                                  )
                                  setValue("ticket_for_self", "Yes")
                                }}
                              />
                              <label
                                className="radioLabel"
                                htmlFor={`ticket_for_self_yes`}
                                style={{ color: "black" }}
                              >
                                Yes
                              </label>
                              <input
                                className="radio"
                                type="radio"
                                id={`ticket_for_self_no`}
                                value="No"
                                {...register("ticket_for_self")}
                                onChange={() => {
                                  setValue(`person[0].prefix`, "")
                                  setValue(`person[0].first_name`, "")
                                  setValue(`person[0].last_name`, "")
                                  setValue(`person[0].suffix`, "")
                                  setValue(`person[0].email`, "")
                                  setValue(`person[0].phone`, "")
                                  setValue(`person[0].street_address_1`, "")
                                  setValue(`person[0].street_address_2`, "")
                                  setValue(`person[0].street_address_3`, "")
                                  setValue(`person[0].city`, "")
                                  setValue(`person[0].other_city`, "")
                                  setValue(`person[0].state`, "")
                                  setValue(`person[0].zip`, "")

                                  setValue("ticket_for_self", "No")
                                }}
                              />
                              <label
                                className="radioLabel"
                                htmlFor={`ticket_for_self_no`}
                                style={{ color: "black" }}
                              >
                                No
                              </label>
                            </fieldset>
                          </Form.Group>
                        </Alert>
                      </Col>
                    </Row>
                  </div>

                  {/* {fields.map((item, index) => { */}
                  {Array.from({ 
                    length: watchObject.number_of_tickets === "other" 
                      ? parseInt(watchObject.other_number_of_tickets) || 0
                      : parseInt(watchObject.number_of_tickets) || 0
                  }).map(
                    (_, index) => {
                      return (
                        <div
                          style={{
                            marginBottom: "2rem",
                            border: "1px solid lightgray",
                            padding: "1rem",
                            borderRadius: "0.5rem",
                          }}
                          key={index}
                        >
                          <Row>
                            <Col>
                              <h4>
                                USS IDAHO Commissioning Ticket Request #
                                {index + 1}
                              </h4>
                            </Col>
                          </Row>
                          <Row class="fade-in">
                            <Col sm={12} md={3}>
                              <Form.Group>
                                <Form.Label>
                                  Prefix
                                </Form.Label>
                                <Form.Control
                                  className="formField"
                                  as="select"
                                  {...register(`person.${index}.prefix`)}
                                >
                                  <option value="">Select...</option>
                                  <option value="Mr.">Mr.</option>
                                  <option value="Mrs.">Mrs.</option>
                                  <option value="Mr. and Mrs.">Mr. and Mrs.</option>
                                  <option value="Ms.">Ms.</option>
                                  <option value="Miss">Miss</option>
                                  <option value="Master">Master</option>
                                  <option value="Dr.">Dr.</option>
                                  <option value="Dr. and Dr.">Dr. and Dr.</option>
                                  <option value="Dr. and Mr.">Dr. and Mr.</option>
                                  <option value="Dr. and Mrs.">Dr. and Mrs.</option>
                                  <option value="The Honorable">The Honorable</option>
                                  <option value="The Honorable and Mr.">The Honorable and Mr.</option>
                                  <option value="The Honorable and Mrs.">The Honorable and Mrs.</option>
                                  <option value="Rear Admiral">Rear Admiral</option>
                                  <option value="Vice Admiral">Vice Admiral</option>
                                  <option value="Admiral">Admiral</option>
                                  <option value="Captain">Captain</option>
                                  <option value="Commander">Commander</option>
                                  <option value="Lieutenant Commander">Lieutenant Commander</option>
                                  <option value="Lieutenant">Lieutenant</option>
                                  <option value="Lieutenant Junior Grade">Lieutenant Junior Grade</option>
                                  <option value="Ensign">Ensign</option>
                                  <option value="Commodore">Commodore</option>
                                  <option value="Command Master Chief">Command Master Chief</option>
                                  <option value="Master Chief Petty Officer">Master Chief Petty Officer</option>
                                  <option value="Senior Chief Petty Officer">Senior Chief Petty Officer</option>
                                  <option value="Chief Petty Officer">Chief Petty Officer</option>
                                  <option value="Petty Officer First Class">Petty Officer First Class</option>
                                  <option value="Petty Officer Second Class">Petty Officer Second Class</option>
                                  <option value="Petty Officer Third Class">Petty Officer Third Class</option>
                                  <option value="Seaman">Seaman</option>
                                  <option value="Seaman Apprentice">Seaman Apprentice</option>
                                  <option value="Seaman Recruit">Seaman Recruit</option>
                                  <option value="Master Chief Petty Officer of the Navy">Master Chief Petty Officer of the Navy</option>
                                  <option value="General">General</option>
                                  <option value="Lieutenant General">Lieutenant General</option>
                                  <option value="Major General">Major General</option>
                                  <option value="Brigadier General">Brigadier General</option>
                                  <option value="Colonel">Colonel</option>
                                  <option value="Lieutenant Colonel">Lieutenant Colonel</option>
                                  <option value="Major">Major</option>
                                  <option value="First Lieutenant">First Lieutenant</option>
                                  <option value="Second Lieutenant">Second Lieutenant</option>
                                  <option value="Chief Warrant Officer 5">Chief Warrant Officer 5</option>
                                  <option value="Chief Warrant Officer 4">Chief Warrant Officer 4</option>
                                  <option value="Chief Warrant Officer 3">Chief Warrant Officer 3</option>
                                  <option value="Chief Warrant Officer 2">Chief Warrant Officer 2</option>
                                  <option value="Warrant Officer 1">Warrant Officer 1</option>
                                  <option value="Master Gunnery Sergeant">Master Gunnery Sergeant</option>
                                  <option value="Sergeant Major">Sergeant Major</option>
                                  <option value="First Sergeant">First Sergeant</option>
                                  <option value="Master Sergeant">Master Sergeant</option>
                                  <option value="Gunnery Sergeant">Gunnery Sergeant</option>
                                  <option value="Staff Sergeant">Staff Sergeant</option>
                                  <option value="Sergeant">Sergeant</option>
                                  <option value="Corporal">Corporal</option>
                                  <option value="Lance Corporal">Lance Corporal</option>
                                  <option value="Private First Class">Private First Class</option>
                                  <option value="Private">Private</option>
                                  <option value="Command Sergeant Major">Command Sergeant Major</option>
                                  <option value="Sergeant First Class">Sergeant First Class</option>
                                  <option value="Specialist">Specialist</option>
                                  <option value="Private Second Class">Private Second Class</option>
                                  <option value="Technical Sergeant">Technical Sergeant</option>
                                  <option value="Senior Airman">Senior Airman</option>
                                  <option value="Airman First Class">Airman First Class</option>
                                  <option value="Airman">Airman</option>
                                  <option value="Airman Basic">Airman Basic</option>
                                  <option value="Command Chief Master Sergeant">Command Chief Master Sergeant</option>
                                  <option value="Chief Master Sergeant">Chief Master Sergeant</option>
                                  <option value="Senior Master Sergeant of the Air Force">Senior Master Sergeant of the Air Force</option>
                                  <option value="Sergeant Major of the Marine Corps">Sergeant Major of the Marine Corps</option>
                                  <option value="Reverend">Reverend</option>
                                  <option value="Bishop">Bishop</option>
                                </Form.Control>
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={4}>
                              <Form.Group>
                                <Form.Label>
                                  First Name
                                  <Required />
                                </Form.Label>
                                {watchObject.ticket_for_self === "Yes" &&
                                index === 0 ? (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    value={watchObject.requester_first_name}
                                    {...register(`person.${index}.first_name`)}
                                  />
                                ) : (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(`person.${index}.first_name`)}
                                  />
                                )}
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={5}>
                              <Form.Group>
                                <Form.Label>
                                  Last Name
                                  <Required />
                                </Form.Label>
                                {watchObject.ticket_for_self === "Yes" &&
                                index === 0 ? (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    value={watchObject.requester_last_name}
                                    {...register(`person.${index}.last_name`)}
                                  />
                                ) : (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(`person.${index}.last_name`)}
                                  />
                                )}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label>Suffix</Form.Label>
                                <Form.Control
                                  className="formField"
                                  as="select"
                                  {...register(`person.${index}.suffix`)}
                                >
                                  <option value="">Select...</option>
                                  <option value="and Family">and Family</option>
                                  <option value="and Guest">and Guest</option>
                                  <option value="II">II</option>
                                  <option value="III">III</option>
                                  <option value="Jr.">Jr.</option>
                                  <option value="Esq.">Esq.</option>
                                  <option value="MD">MD</option>
                                </Form.Control>
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label>Military Branch (if applicable)</Form.Label>
                                <Form.Control
                                  className="formField"
                                  as="select"
                                  {...register(`person.${index}.branch`)}
                                >
                                  <option value="">Select...</option>
                                  <option value="USA">USA</option>
                                  <option value="USA (Ret.)">USA (Ret.)</option>
                                  <option value="USAF">USAF</option>
                                  <option value="USAF (Ret.)">USAF (Ret.)</option>
                                  <option value="USCG">USCG</option>
                                  <option value="USCG (Ret.)">USCG (Ret.)</option>
                                  <option value="USMC">USMC</option>
                                  <option value="USMC (Ret.)">USMC (Ret.)</option>
                                  <option value="USN">USN</option>
                                  <option value="USN (Ret.)">USN (Ret.)</option>
                                  <option value="USNR">USNR</option>
                                  <option value="USNR (Ret.)">USNR (Ret.)</option>
                                  <option value="Other">Other</option>
                                </Form.Control>
                              </Form.Group>
                            </Col>

                            {watch(`person.${index}.branch`) === "Other" && (
                              <Col sm={12} md={6}>
                                <Form.Group>
                                  <Form.Label>Specify Military Branch</Form.Label>
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    placeholder="Enter military branch"
                                    {...register(`person.${index}.other_branch`)}
                                  />
                                </Form.Group>
                              </Col>
                            )}
                          </Row>

                          <Row>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label>
                                  Email Address
                                  <Required />
                                </Form.Label>
                                {watchObject.ticket_for_self === "Yes" &&
                                index === 0 ? (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    value={watchObject.requester_email}
                                    {...register(`person.${index}.email`, {
                                      required: "Email is required.",
                                      pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                        message: "Enter a valid e-mail address",
                                      },
                                    })}
                                  />
                                ) : (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(`person.${index}.email`, {
                                      required: "Email is required.",
                                      pattern: {
                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                                        message: "Enter a valid e-mail address",
                                      },
                                    })}
                                  />
                                )}
                                {errors.person?.[index]?.email && (
                                  <span style={styles.formError}>
                                    {errors.person[index].email.message}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                            <Col sm={12} md={6}>
                              <Form.Group>
                                <Form.Label>
                                  Phone Number (mobile preferred)
                                  <Required />
                                </Form.Label>
                                {watchObject.ticket_for_self === "Yes" &&
                                index === 0 ? (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    value={watchObject.requester_phone}
                                    {...register(`person.${index}.phone`, {
                                      required: "Phone number is required.",
                                      // pattern: {
                                      //   value: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
                                      //   message:
                                      //     "Please enter a valid phone number",
                                      // },
                                    })}
                                  />
                                ) : (
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(`person.${index}.phone`, {
                                      required: "Phone number is required.",
                                      // pattern: {
                                      //   value: /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/,
                                      //   message:
                                      //     "Please enter a valid phone number",
                                      // },
                                    })}
                                  />
                                )}
                                {errors.person?.[index]?.phone && (
                                  <span style={styles.formError}>
                                    {errors.person[index].phone.message}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <Form.Group>
                                <Form.Label>
                                  Relationship to the requester
                                </Form.Label>
                                <Form.Row>
                                  <Col sm={3}>
                                    <Form.Control
                                      as="select"
                                      id="relationship"
                                      {...register(
                                        `person.${index}.relationship`
                                      )}
                                    >
                                      <option value="">Select</option>
                                      <option value="Self">Self</option>
                                      <option value="Colleague">
                                        Colleague
                                      </option>
                                      <option value="Colleague - Other">
                                        Colleague - Other
                                      </option>
                                      <option value="Spouse">Spouse</option>
                                      <option value="Partner">Partner</option>
                                      <option value="Family">Family</option>
                                      <option value="Friend">Friend</option>
                                      <option value="OTHER">OTHER</option>
                                    </Form.Control>
                                  </Col>
                                </Form.Row>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col>
                              <Form.Group>
                                <Form.Label>
                                  Street Address 1
                                  <Required />
                                </Form.Label>
                            {watchObject.ticket_for_self === "Yes" &&
                            index === 0 ? (
                              <Form.Control
                                className="formField"
                                type="text"
                                value={watchObject.requester_street_address_1}
                                {...register(`person.${index}.street_address_1`, {
                                  required: true,
                                })}
                              />
                            ) : (
                              <Form.Control
                                className="formField"
                                type="text"
                                {...register(`person.${index}.street_address_1`, {
                                  required: true,
                                })}
                              />
                            )}
                            {errors.person?.[index]?.street_address_1 && (
                              <span style={styles.formError}>
                                Street Address 1 is required.
                              </span>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
  
                      <Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>Street Address 2</Form.Label>
                            {watchObject.ticket_for_self === "Yes" &&
                            index === 0 ? (
                              <Form.Control
                                className="formField"
                                type="text"
                                value={watchObject.requester_street_address_2}
                                {...register(`person.${index}.street_address_2`)}
                              />
                            ) : (
                              <Form.Control
                                className="formField"
                                type="text"
                                {...register(`person.${index}.street_address_2`)}
                              />
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
  
                      <Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>Street Address 3</Form.Label>
                            {watchObject.ticket_for_self === "Yes" &&
                            index === 0 ? (
                              <Form.Control
                                className="formField"
                                type="text"
                                value={watchObject.requester_street_address_3}
                                {...register(`person.${index}.street_address_3`)}
                              />
                            ) : (
                              <Form.Control
                                className="formField"
                                type="text"
                                {...register(`person.${index}.street_address_3`)}
                              />
                            )}
                          </Form.Group>
                        </Col>
                      </Row>
  
                      <Row>
                        <Col sm={12} md={4}>
                          <Form.Group>
                            <Form.Label>
                              City
                              <Required />
                            </Form.Label>
                            {watchObject.ticket_for_self === "Yes" && index === 0 ? (
                              <Form.Control
                                as="select"
                                value={watchObject.requester_city}
                                {...register(`person.${index}.city`, {
                                  required: true,
                                })}
                              >
                                {CityList().cities.map(cityIndex => (
                                  <option key={cityIndex} value={cityIndex}>
                                    {cityIndex}
                                  </option>
                                ))}
                              </Form.Control>
                            ) : (
                              <Form.Control
                                as="select"
                                defaultValue="Select"
                                {...register(`person.${index}.city`, {
                                  required: true,
                                })}
                              >
                                {CityList().cities.map(cityIndex => (
                                  <option key={cityIndex} value={cityIndex}>
                                    {cityIndex}
                                  </option>
                                ))}
                              </Form.Control>
                            )}
                            {errors.person?.[index]?.city && (
                              <span style={styles.formError}>
                                City is required.
                              </span>
                            )}
                          </Form.Group>
                        </Col>
                        {watchObject.person[index] &&
                          watchObject.person[index].city === "OTHER (Specify)" && (
                          <Col sm={12} md={4}>
                            <Form.Group>
                              <Form.Label>Other City</Form.Label>
                              {watchObject.ticket_for_self === "Yes" && index === 0 ? (
                                <Form.Control
                                  className="formField"
                                  type="text"
                                  value={watchObject.requester_other_city}
                                  {...register(`person.${index}.other_city`)}
                                />
                              ) : (
                                <Form.Control
                                  className="formField"
                                  type="text"
                                  {...register(`person.${index}.other_city`)}
                                />
                              )}
                            </Form.Group>
                          </Col>
                        )}
                        <Col sm={12} md={4}>
                          <Form.Group>
                            <Form.Label>
                              State
                              <Required />
                            </Form.Label>
                            {watchObject.ticket_for_self === "Yes" &&
                            index === 0 ? (
                              <Form.Control
                                as="select"
                                value={watchObject.requester_state}
                                {...register(`person.${index}.state`, {
                                  required: true,
                                })}
                              >
                                {StateList().states.map(stateIndex => (
                                  <option key={stateIndex} value={stateIndex}>
                                    {stateIndex}
                                  </option>
                                ))}
                              </Form.Control>
                            ) : (
                              <Form.Control
                                as="select"
                                defaultValue="Idaho"
                                {...register(`person.${index}.state`, {
                                  required: true,
                                })}
                              >
                                {StateList().states.map(stateIndex => (
                                  <option key={stateIndex} value={stateIndex}>
                                    {stateIndex}
                                  </option>
                                ))}
                              </Form.Control>
                            )}
                            {errors.person?.[index]?.state && (
                              <span style={styles.formError}>
                                State is required.
                              </span>
                            )}
                          </Form.Group>
                        </Col>
                        <Col sm={12} md={4}>
                          <Form.Group>
                            <Form.Label>
                              Zip
                              <Required />
                            </Form.Label>
                            {watchObject.ticket_for_self === "Yes" &&
                            index === 0 ? (
                              <Form.Control
                                className="formField"
                                type="text"
                                value={watchObject.requester_zip}
                                {...register(`person.${index}.zip`, {
                                  required: true,
                                })}
                              />
                            ) : (
                              <Form.Control
                                className="formField"
                                type="text"
                                {...register(`person.${index}.zip`, {
                                  required: true,
                                })}
                              />
                            )}
                            {errors.person?.[index]?.zip && (
                              <span style={styles.formError}>
                                Zip is required.
                              </span>
                            )}
                          </Form.Group>
                        </Col>
                      </Row>

                          <Row>
                            <Col sm={12} md={4}>
                              <Form.Group>
                                <Form.Label>
                                  Date of birth
                                  <Required />
                                </Form.Label>
                                <Form.Control
                                  type="date"
                                  placeholder="dd/mm/yyyy"
                                  name={`person.${index}.date_of_birth`}
                                  {...register(`person.${index}.date_of_birth`, {
                                    required: "Date of birth is required.",
                                  })}
                                />
                                {errors.person?.[index]?.date_of_birth && (
                                  <span style={styles.formError}>
                                    {errors.person[index].date_of_birth.message}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* Commented out temporarily - may be required later
                          <div>
                              <Row>
                                <Col sm={12} md={3}>
                                  <Form.Group>
                                    <Form.Label>City of Birth</Form.Label>
                                    <Form.Control
                                      type="text"
                                      {...register(
                                        `person.${index}.city_of_birth`
                                      )}
                                    />
                                   
                                  </Form.Group>
                                </Col>
                                <Col sm={12} md={3}>
                                  <Form.Group>
                                    <Form.Label>County of Birth</Form.Label>
                                    <Form.Control
                                      type="text"
                                      {...register(
                                        `person.${index}.county_of_birth`
                                      )}
                                    />
                                   
                                  </Form.Group>
                                </Col>
                                <Col sm={12} md={3}>
                                  <Form.Group>
                                    <Form.Label>State of Birth</Form.Label>
                                    <Form.Control
                                      as="select"
                                      defaultValue="Idaho"
                                      {...register(
                                        `person.${index}.state_of_birth`
                                      )}
                                    >
                                      {StateList().states.map(index => (
                                        <option key={index} value={index}>
                                          {index}
                                        </option>
                                      ))}
                                    </Form.Control>
                                   
                                  </Form.Group>
                                </Col>
                                <Col sm={12} md={3}>
                                  <Form.Group>
                                    <Form.Label>Country of Birth</Form.Label>
                                    <Form.Control
                                      type="text"
                                      defaultValue={"United States"}
                                      placeholder="United States"
                                      {...register(
                                        `person.${index}.country_of_birth`
                                      )}
                                    />
                                   
                                  </Form.Group>
                                </Col>
                              </Row>
                          </div>
                          */}
                          <Row>
                            <Col>
                              <Form.Group>
                                <Form.Label>
                                  US citizen?
                                  <Required />
                                </Form.Label>
                                <fieldset>
                                  <input
                                    className="radio"
                                    type="radio"
                                    label="Yes"
                                    name={`person.${index}.us_citizen`}
                                    id={`${index}.us_citizen_yes`}
                                    value="Yes"
                                    {...register(`person.${index}.us_citizen`, {
                                      required: "US Citizen status is required.",
                                    })}
                                  />
                                  <label
                                    className="radioLabel"
                                    htmlFor={`${index}.us_citizen_yes`}
                                  >
                                    Yes
                                  </label>
                                  <input
                                    className="radio"
                                    type="radio"
                                    name={`person.${index}.us_citizen`}
                                    id={`${index}.us_citizen_no`}
                                    value="No"
                                    {...register(`person.${index}.us_citizen`, {
                                      required: "US Citizen status is required.",
                                    })}
                                    style={{ marginLeft: "2rem" }}
                                  />
                                  <label
                                    className="radioLabel"
                                    htmlFor={`${index}.us_citizen_no`}
                                  >
                                    No
                                  </label>
                                </fieldset>
                                {errors.person?.[index]?.us_citizen && (
                                  <span style={styles.formError}>
                                    {errors.person[index].us_citizen.message}
                                  </span>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>
                          {watchObject.person[index] &&
                            watchObject.person[index].us_citizen === "No" && (
                              <Row className="fade-in">
                                <Col sm={12} md={4}>
                                  <Form.Group>
                                    <Form.Label>
                                      If not a US citizen, country of citizenship
                                      <Required />
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      name={`person.${index}.country_of_citizenship`}
                                      {...register(
                                        `person.${index}.country_of_citizenship`,
                                        {
                                          required: "Country of citizenship is required for non-US citizens.",
                                        }
                                      )}
                                    />
                                    {errors.person?.[index]?.country_of_citizenship && (
                                      <span style={styles.formError}>
                                        {errors.person[index].country_of_citizenship.message}
                                      </span>
                                    )}
                                  </Form.Group>
                                </Col>
                                <Col sm={12} md={4}>
                                  <Form.Group>
                                    <Form.Label>
                                      Other Country Passport
                                      <Required />
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      {...register(
                                        `person.${index}.other_country_passport`,
                                        {
                                          required: "Passport country is required for non-US citizens.",
                                        }
                                      )}
                                    />
                                    {errors.person?.[index]?.other_country_passport && (
                                      <span style={styles.formError}>
                                        {errors.person[index].other_country_passport.message}
                                      </span>
                                    )}
                                  </Form.Group>
                                </Col>
                                <Col sm={12} md={4}>
                                  <Form.Group>
                                    <Form.Label>
                                      Other Country Passport Number
                                      <Required />
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      {...register(
                                        `person.${index}.other_country_passport_number`,
                                        {
                                          required: "Passport number is required for non-US citizens.",
                                        }
                                      )}
                                    />
                                    {errors.person?.[index]?.other_country_passport_number && (
                                      <span style={styles.formError}>
                                        {errors.person[index].other_country_passport_number.message}
                                      </span>
                                    )}
                                  </Form.Group>
                                </Col>
                              </Row>
                            )}

                          {watchObject.person[index] &&
                            watchObject.person[index].us_citizen === "No" && (
                              <Row className="fade-in">
                                <Col sm={12} md={6}>
                                  <Form.Group>
                                    <Form.Label>
                                      Are you a US Permanent Resident?
                                      <Required />
                                    </Form.Label>
                                    <fieldset>
                                      <input
                                        className="radio"
                                        type="radio"
                                        name={`person.${index}.permanent_resident`}
                                        id={`${index}.permanent_resident_yes`}
                                        value="Yes"
                                        {...register(
                                          `person.${index}.permanent_resident`,
                                          {
                                            required: "Please indicate if you are a US Permanent Resident.",
                                          }
                                        )}
                                      />
                                      <label
                                        className="radioLabel"
                                        htmlFor={`${index}.permanent_resident_yes`}
                                      >
                                        Yes
                                      </label>
                                      <input
                                        className="radio"
                                        type="radio"
                                        name={`person.${index}.permanent_resident`}
                                        id={`${index}.permanent_resident_no`}
                                        value="No"
                                        {...register(
                                          `person.${index}.permanent_resident`,
                                          {
                                            required: "Please indicate if you are a US Permanent Resident.",
                                          }
                                        )}
                                        style={{ marginLeft: "2rem" }}
                                      />
                                      <label
                                        className="radioLabel"
                                        htmlFor={`${index}.permanent_resident_no`}
                                      >
                                        No
                                      </label>
                                    </fieldset>
                                    {errors.person?.[index]?.permanent_resident && (
                                      <span style={styles.formError}>
                                        {errors.person[index].permanent_resident.message}
                                      </span>
                                    )}
                                  </Form.Group>
                                </Col>
                              </Row>
                            )}

                          {watchObject.person[index] &&
                            watchObject.person[index].us_citizen === "No" &&
                            watchObject.person[index].permanent_resident === "Yes" && (
                              <Row className="fade-in">
                                <Col sm={12} md={6}>
                                  <Form.Group>
                                    <Form.Label>
                                      Green Card Number
                                      <Required />
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      {...register(
                                        `person.${index}.green_card_number`,
                                        {
                                          required: "Green Card number is required for US Permanent Residents.",
                                        }
                                      )}
                                    />
                                    {errors.person?.[index]?.green_card_number && (
                                      <span style={styles.formError}>
                                        {errors.person[index].green_card_number.message}
                                      </span>
                                    )}
                                  </Form.Group>
                                </Col>
                              </Row>
                            )}

                          {watchObject.person[index] &&
                            watchObject.person[index].us_citizen === "Yes" && (
                            <>
                              <Row>
                                <Col sm={12} md={8}>
                                  <Form.Group>
                                    <Form.Label>
                                      Which Nationally Recognized ID do you intend to use to access the Ceremony?
                                      <Required /><br />
                                      <span style={{ fontSize: "0.8rem" }}>(If the potential guest is a minor, enter the ID information of the accompanying  Parent or Guardian.)</span>
                                    </Form.Label>
                                    <fieldset>
                                      <input
                                        className="radio"
                                        type="radio"
                                        name={`person.${index}.id_type`}
                                        id={`${index}.id_type_federal`}
                                        value="Federally Issued ID (DoD, DoW, DoE, DoS, etc.)"
                                        {...register(`person.${index}.id_type`, {
                                          required: "ID type is required.",
                                        })}
                                      />
                                      <label
                                        className="radioLabel"
                                        htmlFor={`${index}.id_type_federal`}
                                      >
                                        Federally Issued ID (DoD, DoW, DoE, DoS, etc.)
                                      </label>
                                      <br />
                                      <input
                                        className="radio"
                                        type="radio"
                                        name={`person.${index}.id_type`}
                                        id={`${index}.id_type_passport`}
                                        value="Passport"
                                        {...register(`person.${index}.id_type`, {
                                          required: "ID type is required.",
                                        })}
                                      />
                                      <label
                                        className="radioLabel"
                                        htmlFor={`${index}.id_type_passport`}
                                      >
                                        Passport
                                      </label>
                                      <br />
                                      <input
                                        className="radio"
                                        type="radio"
                                        name={`person.${index}.id_type`}
                                        id={`${index}.id_type_realid`}
                                        value="REAL ID (Star Card) Driver's License"
                                        {...register(`person.${index}.id_type`, {
                                          required: "ID type is required.",
                                        })}
                                      />
                                      <label
                                        className="radioLabel"
                                        htmlFor={`${index}.id_type_realid`}
                                      >
                                        REAL ID (Star Card) Driver's License
                                      </label>
                                    </fieldset>
                                    {errors.person?.[index]?.id_type && (
                                      <span style={styles.formError}>
                                        {errors.person[index].id_type.message}
                                      </span>
                                    )}
                                  </Form.Group>
                                </Col>
                              </Row>

                          {watchObject.person[index] &&
                            watchObject.person[index].id_type === "REAL ID (Star Card) Driver's License" && (
                            <Row className="fade-in">
                              <Col sm={12} md={6}>
                                <Form.Group>
                                  <Form.Label>
                                    Driver's License State of Issue
                                    <Required />
                                  </Form.Label>
                                  <Form.Control
                                    as="select"
                                    defaultValue="Idaho"
                                    {...register(
                                      `person.${index}.drivers_license_state`, {
                                        required: "Driver's license state is required.",
                                      }
                                    )}
                                  >
                                    {StateList().states.map(index => (
                                      <option key={index} value={index}>
                                        {index}
                                      </option>
                                    ))}
                                  </Form.Control>
                                  {errors.person?.[index]?.drivers_license_state && (
                                    <span style={styles.formError}>
                                      {errors.person[index].drivers_license_state.message}
                                    </span>
                                  )}
                                </Form.Group>
                              </Col>
                              <Col sm={12} md={6}>
                                <Form.Group>
                                  <Form.Label>
                                    Driver's License Number
                                    <Required />
                                  </Form.Label>
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(
                                      `person.${index}.drivers_license_number`, {
                                        required: "Driver's license number is required.",
                                      }
                                    )}
                                  />
                                  {errors.person?.[index]?.drivers_license_number && (
                                    <span style={styles.formError}>
                                      {errors.person[index].drivers_license_number.message}
                                    </span>
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                          )}

                          {watchObject.person[index] &&
                            watchObject.person[index].id_type === "Federally Issued ID (DoD, DoW, DoE, DoS, etc.)" && (
                            <Row className="fade-in">
                              <Col sm={12} md={6}>
                                <Form.Group>
                                  <Form.Label>
                                    Federal ID Type
                                    <Required />
                                  </Form.Label>
                                  <fieldset>
                                    <input
                                      className="radio"
                                      type="radio"
                                      name={`person.${index}.federal_id_type`}
                                      id={`${index}.federal_id_dod`}
                                      value="DoD"
                                      {...register(`person.${index}.federal_id_type`, {
                                        required: "Federal ID type is required.",
                                      })}
                                    />
                                    <label
                                      className="radioLabel"
                                      htmlFor={`${index}.federal_id_dod`}
                                    >
                                      DoD
                                    </label>
                                    <input
                                      className="radio"
                                      type="radio"
                                      name={`person.${index}.federal_id_type`}
                                      id={`${index}.federal_id_dow`}
                                      value="DoW"
                                      {...register(`person.${index}.federal_id_type`, {
                                        required: "Federal ID type is required.",
                                      })}
                                      style={{ marginLeft: "2rem" }}
                                    />
                                    <label
                                      className="radioLabel"
                                      htmlFor={`${index}.federal_id_dow`}
                                    >
                                      DoW
                                    </label>
                                    <input
                                      className="radio"
                                      type="radio"
                                      name={`person.${index}.federal_id_type`}
                                      id={`${index}.federal_id_doe`}
                                      value="DoE"
                                      {...register(`person.${index}.federal_id_type`, {
                                        required: "Federal ID type is required.",
                                      })}
                                      style={{ marginLeft: "2rem" }}
                                    />
                                    <label
                                      className="radioLabel"
                                      htmlFor={`${index}.federal_id_doe`}
                                    >
                                      DoE
                                    </label>
                                    <input
                                      className="radio"
                                      type="radio"
                                      name={`person.${index}.federal_id_type`}
                                      id={`${index}.federal_id_dos`}
                                      value="DoS"
                                      {...register(`person.${index}.federal_id_type`, {
                                        required: "Federal ID type is required.",
                                      })}
                                      style={{ marginLeft: "2rem" }}
                                    />
                                    <label
                                      className="radioLabel"
                                      htmlFor={`${index}.federal_id_dos`}
                                    >
                                      DoS
                                    </label>
                                  </fieldset>
                                  {errors.person?.[index]?.federal_id_type && (
                                    <span style={styles.formError}>
                                      {errors.person[index].federal_id_type.message}
                                    </span>
                                  )}
                                </Form.Group>
                              </Col>
                              <Col sm={12} md={6}>
                                <Form.Group>
                                  <Form.Label>
                                    Federal ID Number
                                    <Required />
                                  </Form.Label>
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(`person.${index}.federal_id_number`, {
                                      required: "Federal ID number is required.",
                                    })}
                                  />
                                  {errors.person?.[index]?.federal_id_number && (
                                    <span style={styles.formError}>
                                      {errors.person[index].federal_id_number.message}
                                    </span>
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                          )}

                          {watchObject.person[index] &&
                            watchObject.person[index].id_type === "Passport" && (
                            <Row className="fade-in">
                              <Col sm={12} md={4}>
                                <Form.Group>
                                  <Form.Label>
                                    US Passport Number
                                    <Required />
                                  </Form.Label>
                                  <Form.Control
                                    type="text"
                                    {...register(
                                      `person.${index}.passport_number`, {
                                        required: "Passport number is required.",
                                      }
                                    )}
                                  />
                                  {errors.person?.[index]?.passport_number && (
                                    <span style={styles.formError}>
                                      {errors.person[index].passport_number.message}
                                    </span>
                                  )}
                                </Form.Group>
                              </Col>
                            </Row>
                          )}
                            </>
                          )}

                          <Row>
                            <Col sm={12} md={3}>
                              <Form.Group>
                                <Form.Label>Veteran?</Form.Label>
                                <fieldset>
                                  <input
                                    className="radio"
                                    type="radio"
                                    label="Yes"
                                    name={`person.${index}.veteran`}
                                    id={`${index}.veteran_yes`}
                                    value="Yes"
                                    {...register(`person.${index}.veteran`)}
                                  />
                                  <label
                                    className="radioLabel"
                                    htmlFor={`${index}.veteran_yes`}
                                  >
                                    Yes
                                  </label>
                                  <input
                                    className="radio"
                                    type="radio"
                                    name={`person.${index}.veteran`}
                                    id={`${index}.veteran_yes`}
                                    value="No"
                                    {...register(`person.${index}.veteran`)}
                                    style={{ marginLeft: "2rem" }}
                                  />
                                  <label
                                    className="radioLabel"
                                    htmlFor={`${index}.veteran_yes`}
                                  >
                                    No
                                  </label>
                                </fieldset>
                              </Form.Group>
                            </Col>
                            {watchObject.person[index] &&
                              watchObject.person[index].veteran === "Yes" && (
                                <>
                                  <Col className="fade-in" sm={12} md={3}>
                                    <Form.Group>
                                      <Form.Label>Branch</Form.Label>
                                      <Form.Row>
                                        <Col>
                                          <Form.Control
                                            as="select"
                                            id="branch"
                                            {...register(
                                              `person.${index}.branch`
                                            )}
                                          >
                                            <option value="">Select</option>
                                            <option value="Navy">Navy</option>
                                            <option value="Marine Corps">
                                              Marine Corps
                                            </option>
                                            <option value="Army">Army</option>
                                            <option value="Air Force">
                                              Air Force
                                            </option>
                                            <option value="Coast Guard">
                                              Coast Guard
                                            </option>
                                            <option value="National Guard">
                                              National Guard
                                            </option>
                                            <option value="Merchant Marine">
                                              Merchant Marine
                                            </option>
                                          </Form.Control>
                                        </Col>
                                      </Form.Row>
                                    </Form.Group>
                                  </Col>
                                  <Col className="fade-in" sm={12} md={3}>
                                    <Form.Group>
                                      <Form.Label>Status</Form.Label>
                                      <Form.Row>
                                        <Col>
                                          <Form.Control
                                            as="select"
                                            id="status"
                                            {...register(
                                              `person.${index}.status`
                                            )}
                                          >
                                            <option value="">Select</option>
                                            <option value="Active Duty">
                                              Active Duty
                                            </option>
                                            <option value="Reserve">
                                              Reserve
                                            </option>
                                            <option value="National Guard">
                                              National Guard
                                            </option>
                                            <option value="Retired (w/ DoD ID)">
                                              Retired (w/ DoD ID)
                                            </option>
                                            <option value="Veteran (no DoD ID)">
                                              Veteran (no DoD ID)
                                            </option>
                                          </Form.Control>
                                        </Col>
                                      </Form.Row>
                                    </Form.Group>
                                  </Col>
                                  <Col className="fade-in" sm={12} md={3}>
                                    <Form.Group>
                                      <Form.Label>Rank</Form.Label>
                                      <Form.Row>
                                        <Col>
                                          <Form.Control
                                            as="select"
                                            id="rank"
                                            {...register(
                                              `person.${index}.rank`
                                            )}
                                          >
                                            <option value="">Select</option>
                                            <option value="ADM">ADM</option>
                                            <option value="VADM">VADM</option>
                                            <option value="RADM">RADM</option>
                                            <option value="RDML">RDML</option>
                                            <option value="CAPT">CAPT</option>
                                            <option value="CDR">CDR</option>
                                            <option value="LCDR">LCDR</option>
                                            <option value="LT">LT</option>
                                            <option value="LTJG">LTJG</option>
                                            <option value="ENS">ENS</option>
                                            <option value="CWO5">CWO5</option>
                                            <option value="CWO4">CWO4</option>
                                            <option value="CWO3">CWO3</option>
                                            <option value="CWO2">CWO2</option>
                                            <option value="WO1">WO1</option>
                                            <option value="MCPON">MCPON</option>
                                            <option value="MPCOCG">
                                              MPCOCG
                                            </option>
                                            <option value="FORCM">FORCM</option>
                                            <option value="FLTCM">FLTCM</option>
                                            <option value="MCPOC">MCPOC</option>
                                            <option value="MCPO">MCPO</option>
                                            <option value="SCPO">SCPO</option>
                                            <option value="CPO">CPO</option>
                                            <option value="PO1">PO1</option>
                                            <option value="PO2">PO2</option>
                                            <option value="PO3">PO3</option>
                                            <option value="SN">SN</option>
                                            <option value="SA">SA</option>
                                            <option value="SR">SR</option>
                                            <option value="Gen">Gen</option>
                                            <option value="LtGen">LtGen</option>
                                            <option value="MajGen">
                                              MajGen
                                            </option>
                                            <option value="BGen">BGen</option>
                                            <option value="Col">Col</option>
                                            <option value="LtCol">LtCol</option>
                                            <option value="Maj">Maj</option>
                                            <option value="Capt">Capt</option>
                                            <option value="1stLt">1stLt</option>
                                            <option value="2ndLt">2ndLt</option>
                                            <option value="CW5">CW5</option>
                                            <option value="CW4">CW4</option>
                                            <option value="CW3">CW3</option>
                                            <option value="CW2">CW2</option>
                                            <option value="WO1">WO1</option>
                                            <option value="SgtMajMarCor">
                                              SgtMajMarCor
                                            </option>
                                            <option value="SgtMaj">
                                              SgtMaj
                                            </option>
                                            <option value="MGySgt">
                                              MGySgt
                                            </option>
                                            <option value="1stSgt">
                                              1stSgt
                                            </option>
                                            <option value="MSgt">MSgt</option>
                                            <option value="GySgt">GySgt</option>
                                            <option value="SSgt">SSgt</option>
                                            <option value="Sgt">Sgt</option>
                                            <option value="Cpl">Cpl</option>
                                            <option value="LCpl">LCpl</option>
                                            <option value="PFC">PFC</option>
                                            <option value="Pvt">Pvt</option>
                                            <option value="GEN">GEN</option>
                                            <option value="LTG">LTG</option>
                                            <option value="MG">MG</option>
                                            <option value="BG">BG</option>
                                            <option value="COL">COL</option>
                                            <option value="LTC">LTC</option>
                                            <option value="MAJ">MAJ</option>
                                            <option value="CPT">CPT</option>
                                            <option value="1LT">1LT</option>
                                            <option value="2LT">2LT</option>
                                            <option value="CW5">CW5</option>
                                            <option value="CW4">CW4</option>
                                            <option value="CW3">CW3</option>
                                            <option value="CW2">CW2</option>
                                            <option value="WO1">WO1</option>
                                            <option value="SMA">SMA</option>
                                            <option value="CSM">CSM</option>
                                            <option value="SGM">SGM</option>
                                            <option value="1SG">1SG</option>
                                            <option value="MSG">MSG</option>
                                            <option value="SFC">SFC</option>
                                            <option value="SSG">SSG</option>
                                            <option value="SGT">SGT</option>
                                            <option value="CPL">CPL</option>
                                            <option value="SPC">SPC</option>
                                            <option value="PFC">PFC</option>
                                            <option value="PV2">PV2</option>
                                            <option value="PVT">PVT</option>
                                            <option value="Gen">Gen</option>
                                            <option value="Lt Gen">
                                              Lt Gen
                                            </option>
                                            <option value="Maj Gen">
                                              Maj Gen
                                            </option>
                                            <option value="Brig Gen">
                                              Brig Gen
                                            </option>
                                            <option value="Col">Col</option>
                                            <option value="Lt Col">
                                              Lt Col
                                            </option>
                                            <option value="Maj">Maj</option>
                                            <option value="Capt">Capt</option>
                                            <option value="1st Lt">
                                              1st Lt
                                            </option>
                                            <option value="2d Lt">2d Lt</option>
                                            <option value="CMSAF">CMSAF</option>
                                            <option value="CCM">CCM</option>
                                            <option value="CMSgt">CMSgt</option>
                                            <option value="SMSgt">SMSgt</option>
                                            <option value="MSgt">MSgt</option>
                                            <option value="TSgt">TSgt</option>
                                            <option value="SSgt">SSgt</option>
                                            <option value="SrA">SrA</option>
                                            <option value="A1C">A1C</option>
                                            <option value="Amn">Amn</option>
                                            <option value="AB">AB</option>
                                          </Form.Control>
                                        </Col>
                                      </Form.Row>
                                    </Form.Group>
                                  </Col>
                                </>
                              )}
                          </Row>

                          <Row>
                            <Col className="fade-in">
                              <Form.Group>
                                <Form.Label>Comments</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  name={`person.${index}.comments`}
                                  {...register(`person.${index}.comments`)}
                                />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col sm={12} md={3}>
                              <Form.Group>
                                <Form.Label>
                                  Veteran Service ORG
                                  <br />
                                  <small>(Check all that apply.)</small>
                                </Form.Label>
                                <div>
                                  <Form.Check
                                    type="checkbox"
                                    label="SUBVET (USSVI)"
                                    value="SUBVET (USSVI)"
                                    {...register(
                                      `person.${index}.veteran_service_org`
                                    )}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    label="American Legion"
                                    value="American Legion"
                                    {...register(
                                      `person.${index}.veteran_service_org`
                                    )}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    label="Veterans of Foreign Wars"
                                    value="Veterans of Foreign Wars"
                                    {...register(
                                      `person.${index}.veteran_service_org`
                                    )}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    label="OTHER (Specify)"
                                    value="OTHER (Specify)"
                                    {...register(
                                      `person.${index}.veteran_service_org`
                                    )}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    label="NONE"
                                    value="NONE"
                                    {...register(
                                      `person.${index}.veteran_service_org`
                                    )}
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                            {watchObject.person[index].veteran_service_org &&
                            watchObject.person[
                              index
                            ].veteran_service_org.includes(
                              "OTHER (Specify)"
                            ) ? (
                              <Col className="fade-in">
                                <Form.Group>
                                  <Form.Label>
                                    Other Veteran Service ORG
                                  </Form.Label>
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(
                                      `person.${index}.other_veteran_service_org`
                                    )}
                                  />
                                </Form.Group>
                              </Col>
                            ) : watchObject.person[index].veteran_service_org &&
                              watchObject.person[
                                index
                              ].veteran_service_org.includes(
                                "SUBVET (USSVI)"
                              ) ? (
                              <Col className="fade-in" sm={12} md="4">
                                <Form.Group>
                                  <Form.Label>Base</Form.Label>
                                  <Form.Row>
                                    <Col>
                                      <Form.Control
                                        as="select"
                                        id="status"
                                        {...register(
                                          `person.${index}.subvet_base`
                                        )}
                                      >
                                        <option value="">Select</option>
                                        <option value="Hawkbill">
                                          Hawkbill
                                        </option>
                                        <option value="Boise">Boise</option>
                                        <option value="Farragut">
                                          Farragut
                                        </option>
                                        <option value="Trout">Trout</option>
                                        <option value="Requin">Requin</option>
                                        <option value="OTHER (Specify)">
                                          OTHER (Specify)
                                        </option>
                                      </Form.Control>
                                    </Col>
                                  </Form.Row>
                                </Form.Group>
                              </Col>
                            ) : null}
                            {watchObject.person[index].subvet_base &&
                            watchObject.person[index].subvet_base ===
                              "OTHER (Specify)" ? (
                              <Col className="fade-in">
                                <Form.Group>
                                  <Form.Label>Other base</Form.Label>
                                  <Form.Control
                                    className="formField"
                                    type="text"
                                    {...register(`person.${index}.other_base`)}
                                  />
                                </Form.Group>
                              </Col>
                            ) : null}
                          </Row>

                          {/* <Row className="text-right">
                        <Col>
                          {watchObject.person && watchObject.person.length > 1 && (
                            <Button
                              variant="danger"
                              btn
                              btn-sm
                              onClick={() => remove(index)}
                            >
                              <img
                                src={MinusSymbol}
                                alt="Add another person"
                                style={{ marginBottom: "3px" }}
                              />{" "}
                              Remove This Person
                            </Button>
                          )}
                        </Col>
                      </Row> */}
                        </div>
                      )
                    }
                  )}
                </div>

                <Row className="mb-4">
                  {/* <Col>
                  <Button
                    variant="success"
                    btn
                    btn-sm
                    onClick={() =>
                      append({
                        email: "",
                        first_name: "",
                        last_name: "",
                        phone: "",
                        relationship: "",
                        street_address_1: "",
                        street_address_2: "",
                        street_address_3: "",
                        city: "",
                        state: "",
                        zip: "",
                        other_city: "",
                        drivers_license_state: "",
                        drivers_license_number: "",
                        star_card: "",
                        date_of_birth: "",
                        city_of_birth: "",
                        state_of_birth: "",
                        country_of_birth: "",
                        us_citizen: "",
                        country_of_citizenship: "",
                        other_country_of_citizenship: "",
                        us_passport: "",
                        passport_number: "",
                        other_country_passport: "",
                        veteran: "",
                        branch: "",
                        status: "",
                        rank: "",
                        dod_id_number: "",
                        comments: "",
                        veteran_service_org: [],
                        other_veteran_service_org: "",
                        subvet_base: "",
                      })
                    }
                    className="mr-4"
                  >
                    <img
                      src={PlusSymbol}
                      alt="Add another person"
                      style={{ marginBottom: "3px" }}
                    />{" "}
                    Add Another Person
                  </Button>
                </Col> */}
                </Row>

                <Row>
                  <Col>
                    <Alert variant="success">
                      <Form.Group style={{ marginBottom: 0 }}>
                        <Form.Label style={{ color: "black" }}>
                          Would you like to request travel booking assistance
                          from Uniglobe Travel for any of the above?
                        </Form.Label>
                        <fieldset>
                          <input
                            className="radio"
                            type="radio"
                            label="Yes"
                            id={`travel_assistance_yes`}
                            name={`travel_assistance_yes`}
                            value="Yes"
                            {...register(`travel_assistance`)}
                          />
                          <label
                            className="radioLabel"
                            htmlFor={`travel_assistance_yes`}
                            style={{ color: "black" }}
                          >
                            Yes
                          </label>
                          <input
                            className="radio"
                            type="radio"
                            id={`travel_assistance_no`}
                            name={`travel_assistance_no`}
                            value="No"
                            {...register(`travel_assistance`)}
                            style={{ marginLeft: "2rem" }}
                          />
                          <label
                            className="radioLabel"
                            htmlFor={`travel_assistance_no`}
                            style={{ color: "black" }}
                          >
                            No
                          </label>
                        </fieldset>
                      </Form.Group>
                      {watchObject.travel_assistance === "Yes" && (
                        <p>
                          Upon submitting this form with the SUBMIT button
                          below, you will then be presented with an additional
                          travel options form for each of your ticket requests.
                        </p>
                      )}
                    </Alert>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div style={{ marginTop: "20px", marginBottom: "20px", padding: "15px", backgroundColor: "#fff3cd", borderRadius: "5px", border: "2px solid #ffc107" }}>
                      <Form.Check
                        type="checkbox"
                        id="email_verification"
                        {...register("email_verified", {
                          required: "You must verify your email address is correct.",
                        })}
                        label={
                          <span>
                            <strong>I have double-checked that my email address is correct:</strong>
                            <br />
                            <span style={{ fontSize: "1.1rem", color: "#0066cc", fontWeight: "600" }}>
                              {watchObject.requester_email || "(email not entered yet)"}
                            </span>
                            <br />
                            <small style={{ color: "#856404" }}>
                              This is where your confirmation and RSVP will be sent.
                            </small>
                          </span>
                        }
                      />
                      {errors.email_verified && (
                        <span style={styles.formError}>
                          {errors.email_verified.message}
                        </span>
                      )}
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Button
                    type="submit"
                    variant="primary"
                    form="new-request-form"
                    disabled={disableSubmit || !watchObject.email_verified}
                    className="submitButton teamButton"
                  >
                    {succeeded
                      ? "Message submitted"
                      : processing
                      ? "SUBMITTING..."
                      : "SUBMIT"}
                  </Button>
                </Row>
              </form>
            </div>
          )}
        </>
      )}
    </>
  )
}
